import { useState, useEffect } from 'react'
// Shared signed-in client + paged reader: WhatsAppConversations' RLS policy is
// authenticated-only and the table is past PostgREST's 1000-row cap (2,621 rows
// on 2026-09-07), so a standalone anon client with one un-paged select showed
// at most the newest 1000 rows.
import { fetchAllRows } from '../../lib/supabase'

// Meta status lifecycle for an outbound message: sent → delivered → read, or
// failed. Only outbound rows carry a delivery status; inbound rows are
// "received" by definition (their Status column still holds the table default).
const isDelivered = (s) => ['delivered', 'read'].includes(String(s || '').toLowerCase())
const isFailed = (s) => ['failed', 'undelivered'].includes(String(s || '').toLowerCase())

export default function DashboardTab({ selectedOrganization, selectedAccount }) {
  const [stats, setStats] = useState({
    total: 0,
    inbound: 0,
    outbound: 0,
    delivered: 0,
    failed: 0,
    pending: 0
  })
  const [recentActivity, setRecentActivity] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [selectedOrganization, selectedAccount])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      const all = await fetchAllRows(
        'WhatsAppConversations',
        'ConversationId, Direction, From, To, Body, MessageType, Status, Created',
        { column: 'Created', ascending: false },
        selectedAccount?.WhatsAppAccountId
          ? { WhatsAppAccountId: selectedAccount.WhatsAppAccountId }
          : undefined
      )

      const total = all.length
      const inbound = all.filter(c => c.Direction === 'inbound').length
      const outboundRows = all.filter(c => c.Direction === 'outbound')
      const outbound = outboundRows.length
      // Delivery outcomes only make sense for outbound messages.
      const delivered = outboundRows.filter(c => isDelivered(c.Status)).length
      const failed = outboundRows.filter(c => isFailed(c.Status)).length
      const pending = Math.max(0, outbound - delivered - failed)

      setStats({ total, inbound, outbound, delivered, failed, pending })
      setRecentActivity(all.slice(0, 10))

    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatColor = (type) => {
    switch(type) {
      case 'total': return 'bg-blue-500'
      case 'inbound': return 'bg-indigo-500'
      case 'outbound': return 'bg-cyan-500'
      case 'delivered': return 'bg-green-500'
      case 'failed': return 'bg-red-500'
      case 'pending': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatIcon = (type) => {
    switch(type) {
      case 'total':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        )
      case 'inbound':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        )
      case 'outbound':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        )
      case 'delivered':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'failed':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'pending':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return null
    }
  }

  const getPercentage = (value, total) => {
    if (total === 0) return 0
    return Math.round((value / total) * 100)
  }

  const getStatusBadge = (status, direction) => {
    const statusLower = String(status || '').toLowerCase()

    // Inbound rows carry the column default, not a Meta report — say what it is.
    if (direction === 'inbound') {
      return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200 rounded-full">Received</span>
    }

    if (statusLower === 'delivered') {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">Delivered</span>
    } else if (statusLower === 'read') {
      return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">Read</span>
    } else if (statusLower === 'failed' || statusLower === 'undelivered') {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full">Failed</span>
    } else if (statusLower === 'sent') {
      return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">Sent</span>
    }

    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200 rounded-full capitalize">{status || 'unknown'}</span>
  }

  const getDirectionBadge = (direction) => {
    if (direction === 'inbound') {
      return <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full">Inbound</span>
    }
    return <span className="px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200 rounded-full">Outbound</span>
  }

  const formatPhoneNumber = (number) => {
    if (!number) return 'Unknown'
    return number.startsWith('+') ? number : `+${number}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">WhatsApp Service Dashboard</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {selectedAccount ? 'Filtered by selected account' : 'Overview of WhatsApp conversations'}
          </p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { key: 'total', label: 'Total Messages', tooltip: 'Total WhatsApp conversations (inbound + outbound)' },
          { key: 'inbound', label: 'Inbound', tooltip: 'Messages received from users', sub: `${getPercentage(stats.inbound, stats.total)}% of total` },
          { key: 'outbound', label: 'Outbound', tooltip: 'Messages sent to users', sub: `${getPercentage(stats.outbound, stats.total)}% of total` },
          { key: 'delivered', label: 'Delivered', tooltip: 'Outbound messages Meta reported as delivered or read', sub: `${getPercentage(stats.delivered, stats.outbound)}% of outbound` },
          { key: 'failed', label: 'Failed', tooltip: 'Outbound messages Meta reported as failed', sub: `${getPercentage(stats.failed, stats.outbound)}% of outbound` },
          { key: 'pending', label: 'Pending', tooltip: 'Outbound messages with no delivered/read/failed report yet', sub: 'Awaiting delivery report' },
        ].map(({ key, label, tooltip, sub }) => (
          <div
            key={key}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 cursor-help transition hover:shadow-lg"
            title={tooltip}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{stats[key]}</p>
                {sub && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>
                )}
              </div>
              <div className={`${getStatColor(key)} p-3 rounded-lg text-white`}>
                {getStatIcon(key)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Last 10 WhatsApp conversations</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Direction
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  From / To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Body
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Time
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {recentActivity.length > 0 ? (
                recentActivity.map((msg) => (
                  <tr key={msg.ConversationId} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getDirectionBadge(msg.Direction)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {msg.Direction === 'inbound'
                        ? formatPhoneNumber(msg.From)
                        : formatPhoneNumber(msg.To)
                      }
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {msg.Body || `[${msg.MessageType}]`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(msg.Status, msg.Direction)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(msg.Created).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    No WhatsApp conversations yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
